/**
 * AnalyticsTracker 组件 - 埋点追踪
 * 
 * 功能：
 * - 记录页面进入时间
 * - 监听滚动事件
 * - 计算滚动深度
 * - 检测是否滑动到底部
 * - 页面退出时上报
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';

// =============================================================================
// 类型定义
// =============================================================================

interface AnalyticsTrackerProps {
  /** 事件ID */
  eventId: string;
  /** 日期 */
  date: string;
  /** 用户ID */
  userId?: number;
  /** 用户名 */
  username?: string;
  /** 是否启用（默认 true） */
  enabled?: boolean;
  /** 子组件 */
  children?: React.ReactNode;
  /** 滚动容器选择器（默认 window） */
  scrollContainerSelector?: string;
  /** 底部阈值（默认 95%） */
  bottomThreshold?: number;
}

interface TrackingState {
  startTime: number;
  maxScrollPercent: number;
  reachedBottom: boolean;
  clickedInterpret: boolean;
  clickedPolymarket: boolean;
}

// =============================================================================
// 组件
// =============================================================================

export function AnalyticsTracker({
  eventId,
  date,
  userId,
  username,
  enabled = true,
  children,
  scrollContainerSelector,
  bottomThreshold = 95,
}: AnalyticsTrackerProps) {
  const { trackEventView, trackInterpretClick, trackPolymarketClick } = useAnalytics({
    userId,
    username,
    enabled,
  });

  // 追踪状态
  const stateRef = useRef<TrackingState>({
    startTime: Date.now(),
    maxScrollPercent: 0,
    reachedBottom: false,
    clickedInterpret: false,
    clickedPolymarket: false,
  });

  // 上报函数
  const reportView = useCallback(() => {
    const state = stateRef.current;
    const duration = Date.now() - state.startTime;
    
    trackEventView(eventId, date, {
      duration_ms: duration,
      scroll_depth_percent: Math.round(state.maxScrollPercent),
      reached_bottom: state.reachedBottom,
      clicked_interpret: state.clickedInterpret,
      clicked_polymarket: state.clickedPolymarket,
    });
  }, [eventId, date, trackEventView]);

  // 滚动处理
  const handleScroll = useCallback(() => {
    const state = stateRef.current;
    
    // 计算滚动深度
    let scrollPercent: number;
    
    if (scrollContainerSelector) {
      // 指定容器滚动
      const container = document.querySelector(scrollContainerSelector);
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;
      } else {
        return;
      }
    } else {
      // window 滚动
      const scrollY = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      scrollPercent = (scrollY / (scrollHeight - clientHeight)) * 100;
    }
    
    // 更新最大滚动深度
    if (scrollPercent > state.maxScrollPercent) {
      state.maxScrollPercent = scrollPercent;
    }
    
    // 检测是否到底
    if (scrollPercent >= bottomThreshold && !state.reachedBottom) {
      state.reachedBottom = true;
    }
  }, [scrollContainerSelector, bottomThreshold]);

  // 点击解读
  const handleInterpretClick = useCallback(() => {
    stateRef.current.clickedInterpret = true;
    trackInterpretClick(eventId, date);
  }, [eventId, date, trackInterpretClick]);

  // 点击 Polymarket
  const handlePolymarketClick = useCallback(() => {
    stateRef.current.clickedPolymarket = true;
    trackPolymarketClick(eventId, date);
  }, [eventId, date, trackPolymarketClick]);

  // 初始化
  useEffect(() => {
    if (!enabled) return;
    
    // 记录开始时间
    stateRef.current.startTime = Date.now();
    
    // 添加滚动监听
    if (scrollContainerSelector) {
      const container = document.querySelector(scrollContainerSelector);
      if (container) {
        container.addEventListener('scroll', handleScroll);
      }
    } else {
      window.addEventListener('scroll', handleScroll);
    }
    
    // 页面离开时上报
    const handleBeforeUnload = () => {
      reportView();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // visibility change 时上报（移动端切换应用）
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        reportView();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      // 清理监听器
      if (scrollContainerSelector) {
        const container = document.querySelector(scrollContainerSelector);
        if (container) {
          container.removeEventListener('scroll', handleScroll);
        }
      } else {
        window.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // 组件卸载时上报
      reportView();
    };
  }, [enabled, scrollContainerSelector, handleScroll, reportView]);

  // 暴露点击追踪方法
  useEffect(() => {
    if (!enabled) return;
    
    // 添加全局事件监听
    const handleInterpretButtonClick = () => {
      handleInterpretClick();
    };
    
    const handlePolymarketButtonClick = () => {
      handlePolymarketClick();
    };
    
    // 自定义事件
    document.addEventListener('analytics:interpret_click', handleInterpretButtonClick);
    document.addEventListener('analytics:polymarket_click', handlePolymarketButtonClick);
    
    return () => {
      document.removeEventListener('analytics:interpret_click', handleInterpretButtonClick);
      document.removeEventListener('analytics:polymarket_click', handlePolymarketButtonClick);
    };
  }, [enabled, handleInterpretClick, handlePolymarketClick]);

  return <>{children}</>;
}

// =============================================================================
// 辅助函数
// =============================================================================

/**
 * 触发解读点击事件
 */
export function triggerInterpretClick() {
  document.dispatchEvent(new CustomEvent('analytics:interpret_click'));
}

/**
 * 触发 Polymarket 点击事件
 */
export function triggerPolymarketClick() {
  document.dispatchEvent(new CustomEvent('analytics:polymarket_click'));
}

// =============================================================================
// 默认导出
// =============================================================================

export default AnalyticsTracker;